import { ReactNode, useEffect, useId, useState } from "react";
import { useModalContext } from "../components/ModalProvider";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { authenticationAtom } from "../State";
import { useAdministrationAPI } from "../AdministrationAPI";
import { userAPI } from "../UserAPI";
import dayjs from "dayjs";
import { Invoice, InvoiceStatus } from "@the-real-insight/tri-model";
import Section from "../components/Section";
import { useUrlPageState } from "../utilities/dataTablePagination";

import { pdf } from "@react-pdf/renderer";
import { useFormats } from "../hooks/formats";
import { downloadFile, downloadInvoice, PDFInvoice } from "../billing/invoice";
import StripePaymentMethod from "../components/StripePaymentMethod";
import MarketplacePaymentSummary from "../components/MarketplacePaymentSummary";
import StripeAccountForm from "../components/StripeAccountForm";
import { legalPersonAddressFields, legalPersonCompanyName } from "../utilities/legalPersonDisplay";

import type { Column, ColumnDef } from "@tanstack/react-table";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CreditCard,
  FileCode,
  FileText,
  Loader2,
  Wallet,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DataTable } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface Properties {}

/** Klickbarer Spaltenkopf — ersetzt die Sortierung der abgelösten Tabelle. */
function SortableHeader({
  column,
  align,
  children,
}: {
  column: Column<any, unknown>;
  align?: "right";
  children: ReactNode;
}) {
  const sorted = column.getIsSorted();

  return (
    <div className={align === "right" ? "flex justify-end" : undefined}>
      <Button
        variant="ghost"
        size="sm"
        className="-mx-3 h-8 gap-1 px-3 font-bold text-foreground"
        onClick={() => column.toggleSorting(sorted === "asc")}
      >
        {children}
        {sorted === "asc" ? (
          <ArrowUp aria-hidden className="h-3.5 w-3.5" />
        ) : sorted === "desc" ? (
          <ArrowDown aria-hidden className="h-3.5 w-3.5" />
        ) : (
          <ArrowUpDown aria-hidden className="h-3.5 w-3.5 opacity-50" />
        )}
      </Button>
    </div>
  );
}

/** Leerzustand der Tabelle: Überschrift, Erklärung und — wo sinnvoll — Handlungsaufforderung. */
function TableEmptyState({
  title,
  hint,
  action,
}: {
  title: string;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 py-4 text-center">
      <div className="text-sm font-semibold text-foreground">{title}</div>
      {hint ? <div className="max-w-prose text-sm text-muted-foreground">{hint}</div> : null}
      {action}
    </div>
  );
}

/** Nur-aktiv-Schalter + Freitextfilter über einer Bestellliste. */
function OrderFilterBar(properties: {
  idPrefix: string;
  onlyActiveLabel: string;
  filterLabel: string;
  filterPlaceholder: string;
  onlyActive: boolean;
  filter: string;
  onOnlyActiveChange: (onlyActive: boolean) => void;
  onFilterChange: (filter: string) => void;
}) {
  const onlyActiveId = `${properties.idPrefix}-only-active`;
  const filterId = `${properties.idPrefix}-filter`;

  return (
    <div className="mb-4 flex min-w-0 flex-wrap items-end gap-4">
      <div className="flex min-w-[200px] flex-1 flex-col gap-1.5">
        <Label htmlFor={filterId}>{properties.filterLabel}</Label>
        <Input
          id={filterId}
          value={properties.filter}
          placeholder={properties.filterPlaceholder}
          onChange={(event) => properties.onFilterChange(event.target.value)}
        />
      </div>
      <div className="flex h-9 items-center gap-2">
        <Checkbox
          id={onlyActiveId}
          checked={properties.onlyActive}
          onCheckedChange={(checked) => properties.onOnlyActiveChange(checked === true)}
        />
        <Label htmlFor={onlyActiveId}>{properties.onlyActiveLabel}</Label>
      </div>
    </div>
  );
}

export default function BillingPanel(_properties: Properties) {
  const { t } = useTranslation();
  const [authentication] = useAtom(authenticationAtom) as any;
  const billingForecastPagination = useUrlPageState("billingForecastPage");
  const inboundInvoicesPagination = useUrlPageState("inboundInvoicesPage");
  const ownOrdersPagination = useUrlPageState("ownOrdersPage");
  const customerOrdersPagination = useUrlPageState("customerOrdersPage");
  const outboundInvoicesPagination = useUrlPageState("outboundInvoicesPage");
  const { pushError } = useModalContext() as any;
  const { currencyFormat } = useFormats();
  const { getInvoiceForecast, getInvoices } = useAdministrationAPI();
  const fieldId = useId();
  const [customer, setCustomer] = useState() as any;
  const [invoices, setInvoices] = useState([]) as any;
  const [outboundInvoices, setOutboundInvoices] = useState([]) as any;
  const [currentBillingPeriodFrom] = useState(dayjs().startOf("M").toDate()) as any;
  const [currentBillingPeriodTo] = useState(dayjs().endOf("M").toDate()) as any;
  const [currentInvoices, setCurrentInvoices] = useState([]) as any;
  const [tab, setTab] = useState("creditors");
  const [consumerOrders, setConsumerOrders] = useState([]) as any;
  const [providerOrders, setProviderOrders] = useState([]) as any;
  const [consumerOrdersOnlyActive, setConsumerOrdersOnlyActive] = useState(false) as any;
  const [consumerOrdersFilter, setConsumerOrdersFilter] = useState("") as any;
  const [providerOrdersOnlyActive, setProviderOrdersOnlyActive] = useState(false) as any;
  const [providerOrdersFilter, setProviderOrdersFilter] = useState("") as any;
  const [loadingInvoices, setLoadingInvoices] = useState(true);
  const [loadingConsumerOrders, setLoadingConsumerOrders] = useState(true);
  const [loadingProviderOrders, setLoadingProviderOrders] = useState(true);
  // Rechnungs-Id, für die gerade ein PDF erzeugt wird — trägt den Button-Spinner.
  const [generatingPdfFor, setGeneratingPdfFor] = useState<string | null>(null);

  useEffect(() => {
    const call = async () => {
      const newCustomer = await userAPI.getCustomer(authentication.customer);

      newCustomer.address = newCustomer.address || {};

      setCustomer(newCustomer);

      const invoiceForecast = await getInvoiceForecast(
        authentication.customer,
        dayjs().startOf("M").toDate(),
        dayjs().endOf("M").toDate()
      );

      // Ensure we always set an array, even if the response is undefined/null
      setCurrentInvoices(Array.isArray(invoiceForecast) ? invoiceForecast : []);

      const invoicesResponse = await getInvoices(
        authentication.customer,
        new Date("2025-06-01T00:00:00"),
        new Date()
      );

      // Process inbound invoices
      const inboundInvoices = ((invoicesResponse?.inbound) || []).sort(
        (a: Invoice, b: Invoice) =>
          b.periodFromDate.getTime() - a.periodFromDate.getTime()
      );

      // Remove duplicates based on id for inbound invoices (use _id as fallback for MongoDB documents)
      const uniqueInboundInvoices = Array.from(
        new Map(
          inboundInvoices.map((invoice: Invoice) => [(invoice as any)._id || invoice.id, invoice])
        ).values()
      );
      setInvoices(uniqueInboundInvoices);

      // Process outbound invoices
      const outboundInvoicesList = ((invoicesResponse?.outbound) || []).sort(
        (a: Invoice, b: Invoice) =>
          b.periodFromDate.getTime() - a.periodFromDate.getTime()
      );

      // Remove duplicates based on id for outbound invoices (use _id as fallback for MongoDB documents)
      const uniqueOutboundInvoices = Array.from(
        new Map(
          outboundInvoicesList.map((invoice: Invoice) => [(invoice as any)._id || invoice.id, invoice])
        ).values()
      );
      setOutboundInvoices(uniqueOutboundInvoices);
      setLoadingInvoices(false);

      try {
        const orders = await userAPI.getConsumerOrders(authentication.customer);
        setConsumerOrders(orders);
      } catch (e: any) {
        console.warn("Could not load consumer orders:", e.message);
      } finally {
        setLoadingConsumerOrders(false);
      }

      try {
        const providerOrdersData = await userAPI.getProviderOrders(
          authentication.customer
        );
        setProviderOrders(providerOrdersData);
      } catch (e: any) {
        console.warn("Could not load provider orders:", e.message);
      } finally {
        setLoadingProviderOrders(false);
      }
    };

    try {
      // Wie im Bestand erreicht ein Fehler des asynchronen Teils dieses catch
      // nicht; der finally-Zweig beendet daher wenigstens die Ladezustände.
      call().finally(() => {
        setLoadingInvoices(false);
        setLoadingConsumerOrders(false);
        setLoadingProviderOrders(false);
      });
    } catch (error: any) {
      pushError(error);
    }
  }, []);

  const downloadInvoicePDF = async (invoice: any) => {
    const invoiceKey = String(invoice._id || invoice.id);
    setGeneratingPdfFor(invoiceKey);
    try {
      const blob = await pdf(
        <PDFInvoice invoice={invoice} customer={customer}></PDFInvoice>
      ).toBlob();

      downloadFile(blob, `${t("billingPanel.invoice")} ${invoice.id}`);
    } finally {
      setGeneratingPdfFor(null);
    }
  };

  /** PDF- und E-Rechnungs-Download einer Rechnung(svorschau). */
  const downloadCell = (invoice: any) => {
    const invoiceKey = String(invoice._id || invoice.id);

    return (
      <TooltipProvider>
        <div className="flex flex-wrap items-center justify-end gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label={t("billingPanel.downloadInvoiceForecastPDF")}
                disabled={generatingPdfFor === invoiceKey}
                onClick={() => downloadInvoicePDF(invoice)}
              >
                {generatingPdfFor === invoiceKey ? (
                  <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
                ) : (
                  <FileText aria-hidden className="h-4 w-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("billingPanel.downloadInvoiceForecastPDF")}</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label={t("billingPanel.downloadInvoiceForecastERechnung")}
                onClick={() => downloadInvoice(invoice)}
              >
                <FileCode aria-hidden className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("billingPanel.downloadInvoiceForecastERechnung")}</TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>
    );
  };

  const forecastColumns: ColumnDef<any>[] = [
    {
      id: "provider",
      accessorFn: (invoice: Invoice) =>
        (invoice.seller ? legalPersonCompanyName(invoice.seller as any) : t("billingPanel.internal")).toLowerCase(),
      header: ({ column }) => (
        <SortableHeader column={column}>{t("billingPanel.providerColumn")}</SortableHeader>
      ),
      cell: ({ row }) => {
        const invoice = row.original as Invoice;

        return (
          <div className="min-w-0 break-words text-sm">
            {invoice.seller ? (
              <>
                <div>{legalPersonCompanyName(invoice.seller as any)}</div>
                <div>{legalPersonAddressFields(invoice.seller as any).city}</div>
              </>
            ) : (
              <div>{t("billingPanel.internal")}</div>
            )}
          </div>
        );
      },
    },
    {
      id: "product",
      accessorFn: (invoice: Invoice) =>
        (invoice.itemGroups || [])
          .map((itemGroup: any) => itemGroup.name || "")
          .join(" ")
          .toLowerCase(),
      header: ({ column }) => (
        <SortableHeader column={column}>{t("billingPanel.productColumn")}</SortableHeader>
      ),
      cell: ({ row }) => (
        <div className="min-w-0 break-words text-sm">
          {/* `ItemGroup` carries no id (see tri-model: it is just `name` +
              `items`), so the former `key={itemGroup.id}` was always undefined
              and React fell back to warning about missing keys. Group names are
              not guaranteed unique within an invoice either, hence name+index. */}
          {(row.original as Invoice).itemGroups.map((itemGroup: any, index: number) => (
            <div key={`${itemGroup.name ?? ""}-${index}`}>{itemGroup.name}</div>
          ))}
        </div>
      ),
    },
    {
      id: "amount",
      accessorFn: (invoice: Invoice) => invoice.subtotal(),
      header: ({ column }) => (
        <SortableHeader column={column} align="right">
          {t("billingPanel.amountColumn")}
        </SortableHeader>
      ),
      cell: ({ row }) => (
        <div className="whitespace-nowrap text-right text-sm">
          {currencyFormat.format((row.original as Invoice).subtotal())}
        </div>
      ),
    },
    {
      id: "download",
      header: () => <span className="sr-only">{t("billingPanel.downloadColumn")}</span>,
      enableSorting: false,
      cell: ({ row }) => downloadCell(row.original),
    },
  ];

  const invoiceColumns: ColumnDef<any>[] = [
    {
      id: "paymentStatus",
      accessorFn: (invoice: Invoice) => (invoice.status === InvoiceStatus.paid ? 1 : 0),
      header: ({ column }) => (
        <SortableHeader column={column}>{t("billingPanel.paymentStatusColumn")}</SortableHeader>
      ),
      cell: ({ row }) => {
        const invoice = row.original as Invoice;
        const paid = invoice.status === InvoiceStatus.paid;

        return (
          <div className={paid ? "text-sm font-bold text-success" : "text-sm font-bold text-destructive"}>
            {paid ? t("billingPanel.paid") : t("billingPanel.open")}
          </div>
        );
      },
    },
    {
      id: "provider",
      accessorFn: (invoice: Invoice) =>
        (invoice.seller ? legalPersonCompanyName(invoice.seller as any) : t("billingPanel.internal")).toLowerCase(),
      header: ({ column }) => (
        <SortableHeader column={column}>{t("billingPanel.providerColumn")}</SortableHeader>
      ),
      cell: ({ row }) => {
        const invoice = row.original as Invoice;

        return (
          <div className="min-w-0 break-words text-sm">
            {invoice.seller
              ? `${legalPersonCompanyName(invoice.seller as any)}, ${legalPersonAddressFields(invoice.seller as any).city}`
              : t("billingPanel.internal")}
          </div>
        );
      },
    },
    {
      id: "date",
      accessorFn: (invoice: Invoice) =>
        invoice.creationDate ? new Date(invoice.creationDate).getTime() : 0,
      header: ({ column }) => (
        <SortableHeader column={column} align="right">
          {t("billingPanel.dateColumn")}
        </SortableHeader>
      ),
      cell: ({ row }) => (
        <div className="whitespace-nowrap text-right text-sm">
          {dayjs((row.original as Invoice).creationDate).format(t("global.dateShort"))}
        </div>
      ),
    },
    {
      id: "period",
      accessorFn: (invoice: Invoice) =>
        invoice.periodFromDate ? new Date(invoice.periodFromDate).getTime() : 0,
      header: ({ column }) => (
        <SortableHeader column={column}>{t("billingPanel.periodColumn")}</SortableHeader>
      ),
      cell: ({ row }) => {
        const invoice = row.original as Invoice;

        return (
          <div className="whitespace-nowrap text-sm">
            {dayjs(invoice.periodFromDate).format(t("global.dateShort"))} -{" "}
            {dayjs(new Date(invoice.periodToDate)).format(t("global.dateShort"))}
          </div>
        );
      },
    },
    {
      id: "amount",
      accessorFn: (invoice: Invoice) => invoice.subtotal(),
      header: ({ column }) => (
        <SortableHeader column={column} align="right">
          {t("billingPanel.amountColumn")}
        </SortableHeader>
      ),
      cell: ({ row }) => (
        <div className="whitespace-nowrap text-right text-sm">
          {currencyFormat.format((row.original as Invoice).subtotal())}
        </div>
      ),
    },
    {
      id: "download",
      header: () => <span className="sr-only">{t("billingPanel.downloadColumn")}</span>,
      enableSorting: false,
      cell: ({ row }) => downloadCell(row.original),
    },
  ];

  /** Bestellspalten; `counterpartColumn` unterscheidet Anbieter- und Käufersicht. */
  const orderColumns = (counterpart: "seller" | "customer"): ColumnDef<any>[] => [
    {
      id: "date",
      accessorFn: (order: any) => (order.orderDate ? new Date(order.orderDate).getTime() : 0),
      header: ({ column }) => (
        <SortableHeader column={column}>{t("billingPanel.dateColumn")}</SortableHeader>
      ),
      cell: ({ row }) => (
        <div className="whitespace-nowrap text-sm">
          {dayjs(row.original.orderDate).format(t("global.dateShort"))}
        </div>
      ),
    },
    {
      id: "counterpart",
      accessorFn: (order: any) => (order[counterpart]?.name || "").toLowerCase(),
      header: ({ column }) => (
        <SortableHeader column={column}>
          {counterpart === "seller" ? t("billingPanel.providerColumn") : t("billingPanel.buyerColumn")}
        </SortableHeader>
      ),
      cell: ({ row }) => (
        <div className="min-w-0 break-words text-sm">
          <div>{row.original[counterpart]?.name}</div>
          <div>{row.original[counterpart]?.address?.city}</div>
        </div>
      ),
    },
    {
      id: "product",
      accessorFn: (order: any) => {
        const names = (order.orderItems || [])
          .map((orderItem: any) => orderItem.marketplaceOffer?.name)
          .filter((name: string) => name)
          .join(", ");
        return (names || "-").toLowerCase();
      },
      header: ({ column }) => (
        <SortableHeader column={column}>{t("billingPanel.productColumn")}</SortableHeader>
      ),
      cell: ({ row }) => (
        <div className="min-w-0 break-words text-sm">
          {(row.original.orderItems || [])
            .map((orderItem: any) => orderItem.marketplaceOffer?.name)
            .filter((name: string) => name)
            .join(", ") || "-"}
        </div>
      ),
    },
    {
      id: "period",
      accessorFn: (order: any) => {
        const fromDate = order.orderItems?.[0]?.plan?.validFromDate;
        return fromDate ? new Date(fromDate).getTime() : 0;
      },
      header: ({ column }) => (
        <SortableHeader column={column}>{t("billingPanel.periodColumn")}</SortableHeader>
      ),
      cell: ({ row }) => (
        <div className="whitespace-nowrap text-sm">
          {row.original.orderItems?.[0]?.plan?.validFromDate
            ? dayjs(row.original.orderItems[0].plan.validFromDate).format(t("global.dateShort"))
            : "-"}
          {" - "}
          {row.original.orderItems?.[0]?.plan?.validToDate
            ? dayjs(row.original.orderItems[0].plan.validToDate).format(t("global.dateShort"))
            : "-"}
        </div>
      ),
    },
    {
      id: "pricingModel",
      accessorFn: (order: any) => (order.orderItems?.[0]?.plan?.name || "-").toLowerCase(),
      header: ({ column }) => (
        <SortableHeader column={column}>{t("billingPanel.pricingModelColumn")}</SortableHeader>
      ),
      cell: ({ row }) => (
        <div className="whitespace-nowrap text-sm">
          {row.original.orderItems?.[0]?.plan?.name || "-"}
        </div>
      ),
    },
  ];

  // Filtering functions
  const filterOrders = (
    orders: any[],
    counterpart: "seller" | "customer",
    onlyActive: boolean,
    filter: string
  ) => {
    let filtered = [...orders];

    // Filter by active status
    if (onlyActive) {
      const now = new Date();
      filtered = filtered.filter((order: any) => {
        const fromDate = order.orderItems?.[0]?.plan?.validFromDate;
        const toDate = order.orderItems?.[0]?.plan?.validToDate;
        if (!fromDate || !toDate) return false;
        const from = new Date(fromDate);
        const to = new Date(toDate);
        return from <= now && now <= to;
      });
    }

    // Filter by search string
    if (filter.trim()) {
      const searchWords = filter.toLowerCase().trim().split(/\s+/);

      filtered = filtered.filter((order: any) => {
        const counterpartName = (order[counterpart]?.name || "").toLowerCase();
        const offerNames = (order.orderItems || [])
          .map((item: any) => item.marketplaceOffer?.name || "")
          .join(" ")
          .toLowerCase();
        const planName = (order.orderItems?.[0]?.plan?.name || "").toLowerCase();

        return searchWords.every((word: string) => {
          return (
            counterpartName.startsWith(word) ||
            offerNames.split(" ").some((offer: string) => offer.startsWith(word)) ||
            planName.startsWith(word)
          );
        });
      });
    }

    return filtered;
  };

  const filteredConsumerOrders = filterOrders(
    consumerOrders,
    "seller",
    consumerOrdersOnlyActive,
    consumerOrdersFilter
  );
  const filteredProviderOrders = filterOrders(
    providerOrders,
    "customer",
    providerOrdersOnlyActive,
    providerOrdersFilter
  );
  const consumerFilterActive = consumerOrdersOnlyActive || consumerOrdersFilter.trim().length > 0;
  const providerFilterActive = providerOrdersOnlyActive || providerOrdersFilter.trim().length > 0;

  const resetConsumerFilter = () => {
    setConsumerOrdersFilter("");
    setConsumerOrdersOnlyActive(false);
  };

  const resetProviderFilter = () => {
    setProviderOrdersFilter("");
    setProviderOrdersOnlyActive(false);
  };

  return (
    <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-4">
      <Tabs value={tab} onValueChange={setTab} className="min-w-0">
        <TabsList className="max-w-full overflow-x-auto">
          <TabsTrigger value="creditors">
            <CreditCard aria-hidden className="h-4 w-4" />
            {t("billingPanel.purchases")}
          </TabsTrigger>
          <TabsTrigger value="debtors">
            <Wallet aria-hidden className="h-4 w-4" />
            {t("billingPanel.sales")}
          </TabsTrigger>
          <TabsTrigger value="payments">
            <Wallet aria-hidden className="h-4 w-4" />
            {t("billingPanel.paymentData")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="creditors">
          <div className="grid min-w-0 grid-cols-1 gap-4 xl:grid-cols-2">
            <div className="flex min-w-0 flex-col gap-4">
              <Section
                title={`${t("billingPanel.currentBillingPeriod")} ${dayjs(currentBillingPeriodFrom).format(
                  t("global.dateShort")
                )} - ${dayjs(currentBillingPeriodTo).format(t("global.dateShort"))}`}
              >
                <p className="mb-4 text-sm">{t("billingPanel.currentPeriodAccumulatedAmounts")}</p>
                <div className="min-w-0 overflow-x-auto">
                  <DataTable
                    columns={forecastColumns}
                    data={currentInvoices}
                    loading={loadingInvoices}
                    page={billingForecastPagination.page}
                    onPageChange={billingForecastPagination.onPageChange}
                    previousAriaLabel={t("global.back")}
                    nextAriaLabel={t("global.next")}
                    emptyMessage={
                      <TableEmptyState
                        title={t("billingPanel.noForecastTitle")}
                        hint={t("billingPanel.noForecastHint")}
                      />
                    }
                  />
                </div>
              </Section>
              <Section title={t("billingPanel.previousInvoices")}>
                <div className="mb-4 flex flex-wrap justify-end">
                  <Button
                    variant="outline"
                    className="max-sm:w-full"
                    onClick={() => {
                      // Der eigentliche Aufruf ist im Bestand auskommentiert:
                      // userAPI.createMissingInvoicesForProject(authentication.customer, project._id)
                    }}
                  >
                    {t("billingPanel.createInvoice")}
                  </Button>
                </div>
                <div className="min-w-0 overflow-x-auto">
                  <DataTable
                    columns={invoiceColumns}
                    data={invoices}
                    loading={loadingInvoices}
                    page={inboundInvoicesPagination.page}
                    onPageChange={inboundInvoicesPagination.onPageChange}
                    previousAriaLabel={t("global.back")}
                    nextAriaLabel={t("global.next")}
                    emptyMessage={
                      <TableEmptyState
                        title={t("billingPanel.noInvoicesTitle")}
                        hint={t("billingPanel.noInvoicesHint")}
                      />
                    }
                  />
                </div>
              </Section>
            </div>
            <div className="flex min-w-0 flex-col gap-4">
              <Section title={t("billingPanel.ownOrders")}>
                <OrderFilterBar
                  idPrefix={`${fieldId}-consumer`}
                  onlyActiveLabel={t("billingPanel.onlyActive")}
                  filterLabel={t("billingPanel.filter")}
                  filterPlaceholder={t("billingPanel.filterPlaceholder")}
                  onlyActive={consumerOrdersOnlyActive}
                  filter={consumerOrdersFilter}
                  onOnlyActiveChange={setConsumerOrdersOnlyActive}
                  onFilterChange={setConsumerOrdersFilter}
                />
                <div className="min-w-0 overflow-x-auto">
                  <DataTable
                    columns={orderColumns("seller")}
                    data={filteredConsumerOrders}
                    loading={loadingConsumerOrders}
                    page={ownOrdersPagination.page}
                    onPageChange={ownOrdersPagination.onPageChange}
                    previousAriaLabel={t("global.back")}
                    nextAriaLabel={t("global.next")}
                    emptyMessage={
                      consumerFilterActive ? (
                        <TableEmptyState
                          title={t("billingPanel.noMatchingOrdersTitle")}
                          hint={t("billingPanel.noMatchingOrdersHint")}
                          action={
                            <Button variant="outline" size="sm" onClick={resetConsumerFilter}>
                              {t("billingPanel.resetFilter")}
                            </Button>
                          }
                        />
                      ) : (
                        <TableEmptyState
                          title={t("billingPanel.noOrdersTitle")}
                          hint={t("billingPanel.noOrdersHint")}
                        />
                      )
                    }
                  />
                </div>
              </Section>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="debtors">
          <div className="flex min-w-0 flex-col gap-4">
            <Section title={t("billingPanel.customerOrders")}>
              <OrderFilterBar
                idPrefix={`${fieldId}-provider`}
                onlyActiveLabel={t("billingPanel.onlyActive")}
                filterLabel={t("billingPanel.filter")}
                filterPlaceholder={t("billingPanel.filterPlaceholder")}
                onlyActive={providerOrdersOnlyActive}
                filter={providerOrdersFilter}
                onOnlyActiveChange={setProviderOrdersOnlyActive}
                onFilterChange={setProviderOrdersFilter}
              />
              <div className="min-w-0 overflow-x-auto">
                <DataTable
                  columns={orderColumns("customer")}
                  data={filteredProviderOrders}
                  loading={loadingProviderOrders}
                  page={customerOrdersPagination.page}
                  onPageChange={customerOrdersPagination.onPageChange}
                  previousAriaLabel={t("global.back")}
                  nextAriaLabel={t("global.next")}
                  emptyMessage={
                    providerFilterActive ? (
                      <TableEmptyState
                        title={t("billingPanel.noMatchingOrdersTitle")}
                        hint={t("billingPanel.noMatchingOrdersHint")}
                        action={
                          <Button variant="outline" size="sm" onClick={resetProviderFilter}>
                            {t("billingPanel.resetFilter")}
                          </Button>
                        }
                      />
                    ) : (
                      <TableEmptyState
                        title={t("billingPanel.noCustomerOrdersTitle")}
                        hint={t("billingPanel.noCustomerOrdersHint")}
                      />
                    )
                  }
                />
              </div>
            </Section>
            <Section title={t("billingPanel.previousInvoices")}>
              <div className="min-w-0 overflow-x-auto">
                <DataTable
                  columns={invoiceColumns}
                  data={outboundInvoices}
                  loading={loadingInvoices}
                  page={outboundInvoicesPagination.page}
                  onPageChange={outboundInvoicesPagination.onPageChange}
                  previousAriaLabel={t("global.back")}
                  nextAriaLabel={t("global.next")}
                  emptyMessage={
                    <TableEmptyState
                      title={t("billingPanel.noOutboundInvoicesTitle")}
                      hint={t("billingPanel.noOutboundInvoicesHint")}
                    />
                  }
                />
              </div>
            </Section>
          </div>
        </TabsContent>

        <TabsContent value="payments">
          <div className="flex min-w-0 flex-col gap-4">
            <div className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-2">
              <Section title={t("billingPanel.paymentData")}>
                <StripePaymentMethod customerId={authentication.customer} />
              </Section>
              <Section title={t("billingPanel.stripeAccountForPaymentReceipt")}>
                <StripeAccountForm customerId={authentication.customer} />
              </Section>
            </div>
            <Section title={t("billingPanel.marketplacePaymentOverview")}>
              <MarketplacePaymentSummary customerId={authentication.customer} />
            </Section>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
